import { FLATBED_EQUIPMENT_CODE, getDimensionMaxByEquipment, MEASUREMENT } from 'src/constants/common';
import * as yup from 'yup';

type Translator = (key: string, opts?: Record<string, unknown>) => string;

interface DimensionValidatorContext {
    equipmentType: string;
    equipmentTypeFullName: string;
    weightMeasurement: string;
}

/**
 * Yup test factory for the per-equipment-type HEIGHT / WIDTH cap on dimension forms.
 *
 * - Flatbed height > cap → step-deck-required hint instead of the generic max message.
 * - maxValue === null (e.g. OTHER equipment height) → skips validation.
 *
 * `getContext` reads the live equipment + weight-measurement values at validation time. Callers either:
 * - capture them via closure when they live in Redux / props (Add Quote, Edit Quote), or
 * - pull them off `ctx.from?.[1]?.value` when they live on the form.
 */
export const createDimensionMaxValidator = (field: 'HEIGHT' | 'WIDTH', t: Translator, getContext: (ctx: yup.TestContext) => DimensionValidatorContext, source = 'DimensionMaxValidator') =>
    function (this: yup.TestContext, value: unknown) {
        if (value === null || value === undefined) return true;

        const { equipmentType, equipmentTypeFullName, weightMeasurement } = getContext(this);
        const maxValue = getDimensionMaxByEquipment(field, equipmentType, weightMeasurement);
        const numericValue = typeof value === 'number' ? value : Number(value);

        const isMetric = weightMeasurement === MEASUREMENT.WEIGHT2.value;
        const unit = isMetric ? ' cm' : '"';
        const isFlatbedHeight = field === 'HEIGHT' && equipmentType === FLATBED_EQUIPMENT_CODE;
        const translationKey = isFlatbedHeight ? 'ValidationMessages.Height_step_deck_required' : field === 'HEIGHT' ? 'ValidationMessages.Height_exceeds_max' : 'ValidationMessages.Width_exceeds_max';

        // eslint-disable-next-line no-console
        console.debug(`[${source}:DimensionMaxValidator]`, {
            path: this.path,
            field,
            equipmentType,
            equipmentTypeFullName,
            weightMeasurement,
            value: numericValue,
            maxValue,
            unit,
            translationKey,
            passed: maxValue === null || numericValue <= maxValue,
        });

        if (maxValue === null || numericValue <= maxValue) return true;

        return this.createError({
            path: `${this.path}`,
            message: t(translationKey, { max: maxValue, unit, equipmentType: equipmentTypeFullName }),
        });
    };
